import { useQuery, useReactiveVar } from '@apollo/client';
import AccountContext from 'context/AccountContext';
import { READ_CORPORATE_ACCOUNT_DISCOUNT } from 'graphql/queries/readCorporateAccountDiscount';
import { useContext, useEffect } from 'react';
import { parkingPaymentCardId } from 'states/common';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import {
  CompanyBenefit,
  Maybe,
  Query,
  QueryReadCorporateAccountDiscountArgs,
} from 'types/generatedSchemaTypes';
import { blockPaymentUsage } from 'utils/paymentUtils';

const useSelectedCompanyBenefit = (
  parkingZoneUid?: string,
  sessionType: 'PARKING_SESSION' | 'CHARGING_SESSION' = 'PARKING_SESSION'
) => {
  const {
    customer: { companyBenefits },
  } = useContext(AccountContext);

  const selectedPaymentMethod = useReactiveVar(parkingPaymentCardId);
  // Selected tab blocks usage of company benefit in charge & park & charge screens
  const selectedTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  // Blocked route blocks usage of company benefit in prepaid buy/edit and permit edit views
  const blockedRoute = usePermitStore(
    (state) => state.blockCompanybenefitInEdit
  );
  const blockBenefit = blockPaymentUsage(selectedTab, blockedRoute);
  const companyBenefit = (companyBenefits || []).find(
    (benefit: Maybe<CompanyBenefit>) => {
      return benefit?.benefitTargetUid === selectedPaymentMethod;
    }
  );

  useEffect(() => {
    if (blockBenefit && companyBenefit) {
      parkingPaymentCardId(undefined);
    }
  }, [blockBenefit, companyBenefit]);

  const corporateAccountUid = companyBenefit?.benefitTargetUid || '';

  const { data: discountAndFeeData } = useQuery<
    Query,
    QueryReadCorporateAccountDiscountArgs
  >(READ_CORPORATE_ACCOUNT_DISCOUNT, {
    variables: {
      input: {
        corporateAccountUid,
        parkingZoneUid: parkingZoneUid ?? '',
        sessionType,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !companyBenefit,
  });

  const {
    transactionFee,
    discountPercent: parkingCorporateAccountDiscountPercent,
    evEnergyDiscountPercent,
  } = discountAndFeeData?.readCorporateAccountDiscount ?? {};

  return {
    hasCorporateAccount: !!corporateAccountUid,
    companyBenefit,
    isUseAllowed: blockBenefit,
    corporateAccountTransactionFee: transactionFee,
    parkingCorporateAccountDiscountPercent,
    evEnergyDiscountPercent,
  };
};

export default useSelectedCompanyBenefit;
